import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { Asset, Comment, Job, ProjectSnapshot, ReviewLink } from '@vidmakr/bindings/types';
import { AlertTriangle, Copy, ExternalLink, KeyRound, Link2, Loader2, Lock, MessageSquare, PenLine, Plus, Trash2 } from 'lucide-react';
import { DEFAULT_POOL, formatTimecode, nextVersionNumber, reviewLinkVersions, uuidv7, versionLabel } from '@vidmakr/shared';
import { CommentBody } from '@/components/mentions';
import { timeAgo } from '@/lib/format';
import { Badge, Button, Input, Modal, Select, Tabs, Toggle, cx } from '@/components/ui';
import { reducers } from '@/lib/stdb';
import { api } from '@/lib/api';
import { attempt, errorMessage, toast } from '@/lib/toast';
import { useOrgPlan } from '@/lib/billing';
import { AssetReviewPanel } from './AssetReview';
import { RenderTargetPicker, useRenderTargets } from './RenderTarget';
import { AssetReviewOverview } from './AssetReviewOverview';

export type AssetShareTab = 'links' | 'overview';

export const reviewUrl = (token: string) => `${window.location.origin}/r/${token}`;

function randomToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function linkState(l: ReviewLink): 'active' | 'off' | 'expired' {
  if (l.revoked) return 'off';
  if (l.expiresAt && Number(l.expiresAt.microsSinceUnixEpoch / 1000n) < Date.now()) return 'expired';
  return 'active';
}

const DAY_MS = 86_400_000;
/** yyyy-mm-dd in local time. */
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** A picked day expires at its end, local time. */
const endOfDay = (day: string) => new Date(`${day}T23:59:59`).getTime();

/** When a link should stop working: a preset, a picked date, or never. */
type Expiry = { kind: 'days'; days: number } | { kind: 'date'; day: string } | { kind: 'never' };
const expiryMs = (e: Expiry) => (e.kind === 'never' ? 0 : e.kind === 'days' ? Date.now() + e.days * DAY_MS : endOfDay(e.day));

/** Preset durations or a date from the calendar, within the plan's longest link. */
function ExpiryPicker({ value, onChange, maxDays }: { value: Expiry; onChange: (e: Expiry) => void; maxDays: number }) {
  const limited = maxDays < 365;
  const today = isoDay(new Date());
  // The end of the last day must stay within the plan's limit.
  const last = isoDay(new Date(Date.now() + (limited ? maxDays - 1 : 365) * DAY_MS));
  const select = value.kind === 'days' ? String(value.days) : value.kind;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Select
        className="w-44"
        value={select}
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'never') onChange({ kind: 'never' });
          else if (v === 'date') onChange({ kind: 'date', day: value.kind === 'date' ? value.day : isoDay(new Date(Date.now() + Math.min(14, maxDays) * DAY_MS)) });
          else onChange({ kind: 'days', days: Number(v) });
        }}
        aria-label="Expiry"
        data-link-expiry
      >
        {[7, 30, 90].map((d) => (
          <option key={d} value={d} disabled={limited && d > maxDays}>
            Expires in {d} days
          </option>
        ))}
        <option value="date">Expires on a date…</option>
        <option value="never" disabled={limited}>
          Never expires
        </option>
      </Select>
      {value.kind === 'date' ? (
        <Input
          type="date"
          className="h-9 w-40"
          min={today}
          max={last}
          value={value.day}
          onChange={(e) => e.target.value && onChange({ kind: 'date', day: e.target.value })}
          onKeyDown={(e) => e.stopPropagation()}
          aria-label="Expiry date"
          data-link-expiry-date
        />
      ) : null}
    </span>
  );
}

const copy = (text: string) => void navigator.clipboard.writeText(text).then(() => toast.success('Link copied'));
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Sets or clears a link's password (the API hashes it; retried while a new link reaches it). */
async function setPassword(linkId: string, password: string | null) {
  for (let i = 0; ; i++) {
    try {
      return await api<{ hasPassword: boolean }>(`/api/review-links/${encodeURIComponent(linkId)}/password`, { method: 'POST', json: { password } });
    } catch (e) {
      if (i >= 10 || !/not found/i.test(errorMessage(e))) throw e;
      await sleep(300);
    }
  }
}

export interface ShareTarget {
  /** Share this finished export. */
  job?: Job;
  /** Share this saved version. */
  versionId?: string;
}

export type ShareTab = 'links' | 'team';

/**
 * Sharing a project for review: one public link (or several) that reviewers
 * keep; each version shared to it shows up there as v1, v2, … once rendered.
 * Shares the edit as it is now by default (saved as the next version and
 * rendered), or a given version or export.
 *
 * The editor adds its team review round (`teamReview`) as a second tab;
 * people who may not manage links (`links` false) see only that.
 */
export function ShareDialog({
  projectId,
  orgId,
  open,
  onClose,
  target = {},
  teamReview,
  links: canShare = true,
  tab: initialTab = 'links',
}: {
  projectId: string;
  orgId: string;
  open: boolean;
  onClose: () => void;
  target?: ShareTarget;
  teamReview?: ReactNode;
  links?: boolean;
  tab?: ShareTab;
}) {
  const [tab, setTab] = useState<ShareTab>(canShare ? initialTab : 'team');
  if (!teamReview || (canShare && tab === 'links')) {
    return (
      <Modal open={open} onClose={onClose} title={teamReview ? <ShareTabs tab="links" onTab={setTab} /> : 'Share for review'} width={600}>
        <ReviewLinksPanel projectId={projectId} orgId={orgId} open={open} target={target} />
      </Modal>
    );
  }
  return (
    <Modal open={open} onClose={onClose} title={canShare ? <ShareTabs tab="team" onTab={setTab} /> : 'Review'} width={600}>
      {teamReview}
    </Modal>
  );
}

function ShareTabs({ tab, onTab }: { tab: ShareTab; onTab: (t: ShareTab) => void }) {
  return (
    <span className="flex items-center gap-3">
      Share
      <Tabs
        value={tab}
        onChange={onTab}
        tabs={[
          { id: 'links', label: <span data-share-tab="links">Review links</span> },
          { id: 'team', label: <span data-share-tab="team">Team review</span> },
        ]}
      />
    </span>
  );
}

/** Review links of a project: pick or create one, share versions to it. */
function ReviewLinksPanel({ projectId, orgId, open, target }: { projectId: string; orgId: string; open: boolean; target: ShareTarget }) {
  const [links, linksReady] = useTable(tables.reviewLink.where((l) => l.projectId.eq(projectId)));
  const [jobRows] = useTable(tables.job.where((j) => j.projectId.eq(projectId)));
  const [snapRows] = useTable(tables.projectSnapshot.where((s) => s.projectId.eq(projectId)));
  const billing = useOrgPlan(orgId);
  const plan = billing?.enabled ? billing.limits.plan : null;
  const pro = !plan || plan.reviewPro;
  const jobs = useMemo(() => new Map(jobRows.map((j) => [j.id, j])), [jobRows]);
  const sorted = useMemo(() => [...links].sort((a, b) => Number(b.createdAt.microsSinceUnixEpoch - a.createdAt.microsSinceUnixEpoch)), [links]);
  const [selectedId, setSelectedId] = useState('');
  const [creating, setCreating] = useState(false);
  // Once the links have loaded: the newest active link, or a new one when there is none.
  const [chosen, setChosen] = useState(false);
  useEffect(() => {
    if (!open) return setChosen(false);
    if (chosen || !linksReady) return;
    const first = sorted.find((l) => linkState(l) === 'active') ?? sorted[0];
    setSelectedId(first?.id ?? '');
    setCreating(!first);
    setChosen(true);
  }, [open, linksReady, chosen]); // eslint-disable-line react-hooks/exhaustive-deps
  // A link that was just created becomes the selected one.
  useEffect(() => {
    if (selectedId && !links.some((l) => l.id === selectedId) && !creating) setSelectedId(sorted[0]?.id ?? '');
  }, [links]); // eslint-disable-line react-hooks/exhaustive-deps
  const selected = links.find((l) => l.id === selectedId) ?? null;
  const targetVersion = target.versionId ? snapRows.find((s) => s.id === target.versionId) : undefined;
  const nextNumber = nextVersionNumber(snapRows);
  const what = target.job ? `the export “${target.job.title}”` : targetVersion ? versionLabel(targetVersion) : `the current edit (saved as v${nextNumber})`;

  return (
    <>
      <p className="mb-4 text-sm text-muted">
        Send one link to reviewers. They watch without an account, comment on exact frames{pro ? ', draw on the picture, and approve or ask for changes' : ''}. Share new
        versions to the same link as you keep editing; they see the newest first and can go back to earlier ones.
      </p>
      {sorted.length ? (
        <div className="mb-3 flex items-center gap-2">
          <Select className="min-w-0 flex-1" value={creating ? '' : selectedId} onChange={(e) => (setSelectedId(e.target.value), setCreating(false))} aria-label="Review link" data-review-link-select>
            {creating ? <option value="">New link</option> : null}
            {sorted.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label} · {reviewLinkVersions(l).length} version{reviewLinkVersions(l).length === 1 ? '' : 's'}
                {linkState(l) !== 'active' ? ` (${linkState(l) === 'off' ? 'off' : 'expired'})` : ''}
              </option>
            ))}
          </Select>
          {!creating ? (
            <Button size="sm" variant="secondary" onClick={() => setCreating(true)} data-new-review-link>
              <Plus size={13} /> New link
            </Button>
          ) : null}
        </div>
      ) : null}
      {creating ? (
        <NewLink
          projectId={projectId}
          orgId={orgId}
          target={target}
          renders={!target.job && !targetVersion?.jobId}
          what={what}
          maxDays={plan?.reviewLinkMaxDays ?? 365}
          pro={pro}
          onCreated={(id) => {
            setSelectedId(id);
            setCreating(false);
          }}
          onCancel={sorted.length ? () => setCreating(false) : undefined}
        />
      ) : !chosen ? (
        <div className="py-8 text-center text-xs text-faint">Loading…</div>
      ) : selected ? (
        <LinkCard link={selected} jobs={jobs} snapshots={snapRows} target={target} what={what} pro={pro} maxDays={plan?.reviewLinkMaxDays ?? 365} />
      ) : null}
    </>
  );
}

/** Puts the target (or the current edit) on a link. */
async function shareTo(link: { id: string }, target: ShareTarget, name: string, render: { format: string; pool: string }) {
  if (target.job) return reducers().setReviewLinkVersion({ id: link.id, jobId: target.job.id, present: true });
  return reducers().shareVersion({ linkId: link.id, snapshotId: target.versionId ?? '', name, format: render.format, pool: render.pool });
}

/**
 * Format and workers of a review render, picked like an export's: the
 * organization's own GPU workers when one is online, else the cloud. WebM
 * (VP9) renders on the CPU workers.
 */
function useReviewRender(orgId: string) {
  const [format, setFormat] = useState('mp4_h264');
  const [picked, setPool] = useState('');
  const { targets, preferred } = useRenderTargets(orgId, picked);
  const gpuOk = format !== 'webm_vp9';
  const wanted = picked || preferred;
  const pool = !gpuOk && targets.find((o) => o.id === wanted)?.gpu ? DEFAULT_POOL : wanted;
  const locked = !!targets.find((o) => o.id === pool)?.locked;
  return { format, setFormat, pool, setPool, targets, gpuOk, locked };
}

function FormatSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Select className="h-8 w-auto text-xs" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Video format" data-share-format>
      <option value="mp4_h264">MP4</option>
      <option value="webm_vp9">WebM</option>
    </Select>
  );
}

function NewLink({
  projectId,
  orgId,
  target,
  renders,
  what,
  maxDays,
  pro,
  onCreated,
  onCancel,
}: {
  projectId: string;
  orgId: string;
  target: ShareTarget;
  /** Sharing renders a new version (not an export or an already rendered one). */
  renders: boolean;
  what: string;
  maxDays: number;
  pro: boolean;
  onCreated: (id: string) => void;
  onCancel?: () => void;
}) {
  const [label, setLabel] = useState('');
  const [comments, setComments] = useState(true);
  const [decisions, setDecisions] = useState(true);
  const [expiry, setExpiry] = useState<Expiry>({ kind: 'days', days: Math.min(30, maxDays) });
  const [password, setPasswordText] = useState('');
  const [name, setName] = useState('');
  const render = useReviewRender(orgId);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    const id = uuidv7();
    const token = randomToken();
    try {
      await reducers().createReviewLink({
        id,
        token,
        projectId,
        jobId: target.job?.id ?? '',
        label: label.trim(),
        allowComments: comments,
        allowDecisions: decisions && pro,
        expiresInDays: 0,
        expiresAtMs: expiryMs(expiry),
      });
      if (!target.job) await shareTo({ id }, target, name, render);
      if (password) await setPassword(id, password);
      copy(reviewUrl(token));
      onCreated(id);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-xl border border-line bg-panel-2 p-3" data-new-link-form>
      <Input placeholder="Label for reviewers (default: the project name)" value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
      {!target.job && !target.versionId ? (
        <Input placeholder="Version name (optional, e.g. First cut)" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.stopPropagation()} data-share-version-name />
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <Toggle checked={comments} onChange={setComments} label="Comments" />
        <span title={pro ? undefined : 'Approvals are part of Pro'}>
          <Toggle checked={decisions && pro} onChange={setDecisions} disabled={!pro} label="Approvals" />
        </span>
        <ExpiryPicker value={expiry} onChange={setExpiry} maxDays={maxDays} />
      </div>
      <div className="flex items-center gap-2">
        <Lock size={14} className="shrink-0 text-muted" />
        <Input
          type="password"
          autoComplete="new-password"
          placeholder={pro ? 'Password (optional)' : 'Password protection is part of Pro'}
          disabled={!pro}
          value={password}
          onChange={(e) => setPasswordText(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          data-new-link-password
        />
      </div>
      {renders ? <RenderTargetPicker targets={render.targets} pool={render.pool} onPool={render.setPool} gpuOk={render.gpuOk} /> : null}
      <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
        <span className="min-w-0 flex-1 text-xs text-muted">Shares {what}{target.job ? '' : ', rendered at up to 1080p'}.</span>
        {!target.job ? <FormatSelect value={render.format} onChange={render.setFormat} /> : null}
        {onCancel ? (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button size="sm" variant="primary" loading={busy} disabled={(!!password && password.length < 4) || (renders && render.locked)} onClick={() => void create()} data-create-review-link>
          <Link2 size={14} /> Create link
        </Button>
      </div>
    </div>
  );
}

function LinkCard({
  link,
  jobs,
  snapshots,
  target,
  what,
  pro,
  maxDays,
  fps = 30,
  focusCommentId,
}: {
  /** Frame rate for timecodes in an asset link's conversation. */
  fps?: number;
  /** A comment of the link's conversation to show (opened from a notification). */
  focusCommentId?: string;
  maxDays: number;
  link: ReviewLink;
  jobs: Map<string, Job>;
  snapshots: readonly ProjectSnapshot[];
  target: ShareTarget;
  what: string;
  pro: boolean;
}) {
  const state = linkState(link);
  const versions = reviewLinkVersions(link);
  const [name, setName] = useState('');
  const render = useReviewRender(link.orgId);
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState<string | null>(null);
  const update = (patch: Partial<Pick<ReviewLink, 'allowComments' | 'revoked' | 'allowDecisions'>>) =>
    void attempt(() => reducers().updateReviewLink({ id: link.id, allowComments: link.allowComments, revoked: link.revoked, allowDecisions: link.allowDecisions, ...patch }));
  const versionOf = (jobId: string) => snapshots.find((s) => s.jobId === jobId);
  // Already on this link?
  const targetJob = target.job?.id ?? (target.versionId ? snapshots.find((s) => s.id === target.versionId)?.jobId : undefined);
  const already = !!targetJob && versions.includes(targetJob);
  const renders = !target.job && !targetJob;
  const needsPro = versions.length > 0 && !pro;
  const share = async () => {
    setBusy(true);
    if (await attempt(() => shareTo(link, target, name, render), `Shared to “${link.label}”`)) setName('');
    setBusy(false);
  };
  const savePassword = async (value: string | null) => {
    try {
      await setPassword(link.id, value);
      toast.success(value ? 'Password set' : 'Password removed');
      setPw(null);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const expires = link.expiresAt ? new Date(Number(link.expiresAt.microsSinceUnixEpoch / 1000n)) : null;
  const [editingExpiry, setEditingExpiry] = useState<Expiry | null>(null);
  const saveExpiry = async () => {
    if (!editingExpiry) return;
    const ok = await attempt(() => reducers().setReviewLinkExpiry({ id: link.id, expiresAtMs: expiryMs(editingExpiry) }), 'Expiry updated');
    if (ok) setEditingExpiry(null);
  };
  return (
    <div className="space-y-3" data-review-link={link.id}>
      <div className="rounded-xl border border-line bg-panel-2 p-3">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
          <span className="truncate font-medium">{link.label}</span>
          <Badge tone={state === 'active' ? 'ok' : 'neutral'}>{state === 'active' ? 'Active' : state === 'off' ? 'Off' : 'Expired'}</Badge>
          {link.hasPassword ? (
            <Badge tone="accent">
              <Lock size={10} /> Password
            </Badge>
          ) : null}
          <button
            className="text-[11px] text-faint underline-offset-2 hover:text-fg hover:underline"
            title="Change when the link stops working"
            onClick={() => setEditingExpiry(editingExpiry ? null : expires ? { kind: 'date', day: isoDay(new Date(Math.max(expires.getTime(), Date.now()))) } : { kind: 'never' })}
            data-link-expiry-edit
          >
            {expires ? `${state === 'expired' ? 'expired' : 'until'} ${expires.toLocaleDateString()}` : 'no expiry'} · change
          </button>
        </div>
        {editingExpiry ? (
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <ExpiryPicker value={editingExpiry} onChange={setEditingExpiry} maxDays={maxDays} />
            <Button size="sm" variant="primary" onClick={() => void saveExpiry()} data-link-expiry-save>
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingExpiry(null)}>
              Cancel
            </Button>
          </div>
        ) : null}
        <div className="flex items-center gap-2">
          <Input readOnly value={reviewUrl(link.token)} className="font-mono text-xs" onFocus={(e) => e.target.select()} data-review-url />
          <Button size="sm" variant="primary" onClick={() => copy(reviewUrl(link.token))} data-copy-review-link>
            <Copy size={13} /> Copy
          </Button>
          <a href={reviewUrl(link.token)} target="_blank" rel="noreferrer" title="Open as a reviewer">
            <Button size="icon-sm" variant="ghost">
              <ExternalLink size={14} />
            </Button>
          </a>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-1 gap-y-1">
          <Button size="sm" variant="ghost" onClick={() => update({ allowComments: !link.allowComments })}>
            {link.allowComments ? 'Comments on' : 'Comments off'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={!pro && !link.allowDecisions}
            title={!pro ? 'Approvals are part of Pro' : undefined}
            onClick={() => update({ allowDecisions: !link.allowDecisions })}
            data-link-approvals={link.allowDecisions ? 'on' : 'off'}
          >
            {link.allowDecisions ? 'Approvals on' : 'Approvals off'}
          </Button>
          <Button size="sm" variant="ghost" disabled={!pro && !link.hasPassword} title={!pro ? 'Password protection is part of Pro' : undefined} onClick={() => setPw(pw === null ? '' : null)} data-link-password>
            <KeyRound size={13} /> {link.hasPassword ? 'Change password' : 'Add password'}
          </Button>
          {link.hasPassword ? (
            <Button size="sm" variant="ghost" onClick={() => void savePassword(null)}>
              Remove password
            </Button>
          ) : null}
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={() => update({ revoked: !link.revoked })}>
            {link.revoked ? 'Turn on' : 'Turn off'}
          </Button>
          <Button size="icon-sm" variant="ghost" title="Delete link" onClick={() => void attempt(() => reducers().deleteReviewLink({ id: link.id }))}>
            <Trash2 size={14} />
          </Button>
        </div>
        {pw !== null ? (
          <div className="mt-2 flex items-center gap-2">
            <Input
              type="password"
              autoComplete="new-password"
              placeholder="New password (4+ characters)"
              value={pw}
              autoFocus
              onChange={(e) => setPw(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter' && pw.length >= 4) void savePassword(pw);
              }}
              data-link-password-input
            />
            <Button size="sm" variant="primary" disabled={pw.length < 4} onClick={() => void savePassword(pw)} data-link-password-save>
              Save
            </Button>
          </div>
        ) : null}
      </div>

      {link.assetId ? <LinkConversation link={link} fps={fps} focusId={focusCommentId} /> : null}
      <div className={cx('rounded-xl border border-line', link.assetId && 'hidden')}>
        <div className="border-b border-line px-3 py-2 text-xs font-semibold text-muted">Versions on this link</div>
        {versions.length ? (
          <div className="divide-y divide-line">
            {[...versions].reverse().map((jobId) => {
              const j = jobs.get(jobId);
              const v = versionOf(jobId);
              return (
                <div key={jobId} className="flex items-center gap-2 px-3 py-2 text-sm" data-link-version={jobId}>
                  <span className="min-w-0 flex-1 truncate">{v ? versionLabel(v) : `Export · ${j?.title ?? ''}`}</span>
                  {!j ? (
                    <span className="text-xs text-faint">deleted</span>
                  ) : j.status === 'done' ? (
                    <span className="text-xs text-ok">Ready</span>
                  ) : j.status === 'failed' || j.status === 'canceled' ? (
                    <span className="flex items-center gap-1 text-xs text-danger" title={j.error}>
                      <AlertTriangle size={12} /> Render {j.status}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-muted" data-link-version-rendering>
                      <Loader2 size={12} className="animate-spin" /> Rendering {Math.round(j.progress * 100)}%
                    </span>
                  )}
                  {versions.length > 1 ? (
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      title="Take off the link"
                      onClick={() => void attempt(() => reducers().setReviewLinkVersion({ id: link.id, jobId, present: false }))}
                    >
                      <Trash2 size={12} />
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-3 py-4 text-center text-xs text-faint">No versions yet.</div>
        )}
        <div className="flex flex-wrap items-center gap-2 border-t border-line bg-panel-2 px-3 py-2.5">
          {already ? (
            <span className="text-xs text-muted">This is already on the link.</span>
          ) : (
            <>
              {renders ? <RenderTargetPicker className="w-full" targets={render.targets} pool={render.pool} onPool={render.setPool} gpuOk={render.gpuOk} /> : null}
              {!target.job && !target.versionId ? (
                <Input
                  className="h-8 w-44 text-xs"
                  placeholder="Version name (optional)"
                  value={name}
                  maxLength={120}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  data-share-version-name
                />
              ) : null}
              {!target.job ? <FormatSelect value={render.format} onChange={render.setFormat} /> : null}
              <div className="flex-1" />
              <Button
                size="sm"
                variant="primary"
                loading={busy}
                disabled={needsPro || state !== 'active' || (renders && render.locked)}
                title={needsPro ? 'More versions on one link are part of Pro; create a new link instead' : undefined}
                onClick={() => void share()}
                data-add-review-version
              >
                <Plus size={13} /> {target.job ? 'Add this export' : target.versionId ? `Add ${what}` : `Share current edit as v${nextVersionNumber(snapshots)}`}
              </Button>
            </>
          )}
        </div>
        {needsPro && !already ? <p className={cx('px-3 pb-2 text-[11px] text-faint')}>On your plan a link holds one version: create a new link for the next one.</p> : null}
      </div>
    </div>
  );
}

/** The Exports page's share button: the same dialog, sharing that export. */
export function ShareReviewModal({ job, open, onClose }: { job: Job; open: boolean; onClose: () => void }) {
  return <ShareDialog projectId={job.projectId} orgId={job.orgId} open={open} onClose={onClose} target={{ job }} />;
}

/**
 * A review link's conversation for the team: what guests wrote through the
 * link, with replies that guests see too. Used for links to an asset, whose
 * comments have no project to show in.
 */
export function LinkConversation({ link, fps, focusId }: { link: ReviewLink; fps: number; focusId?: string }) {
  const [rows] = useTable(tables.comment.where((c) => c.linkId.eq(link.id)));
  // Scroll to the thread a notification pointed at, once it has loaded.
  const focused = useRef<HTMLDivElement | null>(null);
  const focusLoaded = !!focusId && rows.some((c) => c.id === focusId);
  useEffect(() => {
    if (focusLoaded) focused.current?.scrollIntoView({ block: 'nearest' });
  }, [focusLoaded]);
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const roots = useMemo(
    () => rows.filter((c) => !c.parentId).sort((a, b) => (a.timeUs ?? Infinity) - (b.timeUs ?? Infinity) || Number(a.createdAt.microsSinceUnixEpoch - b.createdAt.microsSinceUnixEpoch)),
    [rows],
  );
  const replies = (id: string) => rows.filter((c) => c.parentId === id).sort((a, b) => Number(a.createdAt.microsSinceUnixEpoch - b.createdAt.microsSinceUnixEpoch));
  const send = async () => {
    if (!body.trim()) return;
    const ok = await attempt(() => reducers().addLinkComment({ id: uuidv7(), linkId: link.id, parentId: replyTo ?? undefined, body, timeUs: undefined, endUs: undefined, drawing: '' }));
    if (ok) {
      setBody('');
      setReplyTo(null);
    }
  };
  return (
    <div className="rounded-xl border border-line" data-link-conversation={link.id}>
      <div className="flex items-center gap-1.5 border-b border-line px-3 py-2 text-xs font-semibold text-muted">
        <MessageSquare size={13} /> Comments through this link
      </div>
      {roots.length ? (
        <div className="max-h-72 divide-y divide-line overflow-y-auto">
          {roots.map((c) => (
            <div
              key={c.id}
              ref={c.id === focusId ? focused : undefined}
              className={cx('px-3 py-2 text-sm', c.resolved && 'opacity-60', c.id === focusId && 'bg-accent/10')}
              data-link-comment={c.id}
              data-focused={c.id === focusId || undefined}
            >
              <ConversationLine c={c} fps={fps} />
              {replies(c.id).map((r) => (
                <div key={r.id} className="ml-4 mt-1.5 border-l border-line pl-2">
                  <ConversationLine c={r} fps={fps} />
                </div>
              ))}
              <div className="mt-1 flex gap-3 text-[11px] text-muted">
                <button className="hover:text-fg" onClick={() => setReplyTo(replyTo === c.id ? null : c.id)}>
                  {replyTo === c.id ? 'Cancel reply' : 'Reply'}
                </button>
                <button className="hover:text-ok" onClick={() => void attempt(() => reducers().resolveComment({ id: c.id, resolved: !c.resolved }))}>
                  {c.resolved ? 'Reopen' : 'Resolve'}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="px-3 py-4 text-center text-xs text-faint">No comments yet. Reviewers' comments show up here.</div>
      )}
      <div className="flex items-center gap-2 border-t border-line bg-panel-2 px-3 py-2.5">
        <Input
          className="h-8 text-xs"
          placeholder={replyTo ? `Reply to ${rows.find((c) => c.id === replyTo)?.authorName ?? ''}…` : 'Write to the reviewers…'}
          value={body}
          maxLength={5000}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') void send();
          }}
          data-link-comment-input
        />
        <Button size="sm" variant="primary" disabled={!body.trim()} onClick={() => void send()} data-link-comment-send>
          Send
        </Button>
      </div>
    </div>
  );
}

function ConversationLine({ c, fps }: { c: Comment; fps: number }) {
  return (
    <div className="min-w-0">
      <span className="font-medium">{c.authorName}</span>
      {c.timeUs !== undefined ? <span className="ml-1.5 rounded bg-accent/15 px-1 font-mono text-[10px] text-accent-2">{formatTimecode(c.timeUs, fps)}</span> : null}
      {c.drawing ? <PenLine size={11} className="ml-1 inline text-warn" /> : null}
      <span className="ml-1.5 text-[11px] text-faint">{timeAgo(c.createdAt)}</span>
      <CommentBody body={c.body} className="block whitespace-pre-wrap break-words text-muted" />
    </div>
  );
}

/**
 * Shares one asset of the media library for review: links that play the
 * file itself (no project or export needed), each with its own conversation,
 * and the asset's review (approvals and change requests, from guests on links
 * that allow them and from the team). `commentId` opens the link whose
 * conversation holds that comment (from a notification).
 */
export function ShareAssetDialog({
  asset,
  open,
  onClose,
  commentId = '',
  tab: initialTab = 'links',
}: {
  asset: Asset;
  open: boolean;
  onClose: () => void;
  commentId?: string;
  /** Links (create, manage, one conversation) or the overview of every link's feedback and the rounds. */
  tab?: AssetShareTab;
}) {
  const [tab, setTab] = useState<AssetShareTab>(commentId ? 'links' : initialTab);
  const [links, linksReady] = useTable(tables.reviewLink.where((l) => l.assetId.eq(asset.id)));
  const [focusRows] = useTable(tables.comment.where((c) => c.id.eq(commentId)));
  const focusLinkId = focusRows[0]?.linkId ?? '';
  const billing = useOrgPlan(asset.orgId);
  const plan = billing?.enabled ? billing.limits.plan : null;
  const pro = !plan || plan.reviewPro;
  const maxDays = plan?.reviewLinkMaxDays ?? 365;
  const sorted = useMemo(() => [...links].sort((a, b) => Number(b.createdAt.microsSinceUnixEpoch - a.createdAt.microsSinceUnixEpoch)), [links]);
  const [selectedId, setSelectedId] = useState('');
  const [creating, setCreating] = useState(false);
  const [chosen, setChosen] = useState(false);
  useEffect(() => {
    if (!open || chosen || !linksReady) return;
    const first = sorted.find((l) => linkState(l) === 'active') ?? sorted[0];
    setSelectedId(first?.id ?? '');
    setCreating(!first);
    setChosen(true);
  }, [open, linksReady, chosen]); // eslint-disable-line react-hooks/exhaustive-deps
  // A notification's comment: show its link's conversation.
  useEffect(() => {
    if (!focusLinkId || !links.some((l) => l.id === focusLinkId)) return;
    setSelectedId(focusLinkId);
    setCreating(false);
  }, [focusLinkId, links.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const selected = links.find((l) => l.id === selectedId) ?? null;
  const ready = asset.status === 'ready' && ['video', 'audio', 'image'].includes(asset.kind);
  const overview = ready && tab === 'overview';
  return (
    <Modal
      open={open}
      onClose={onClose}
      width={overview ? 1080 : 600}
      title={
        ready ? (
          <span className="flex items-center gap-3">
            Share for review
            <Tabs
              value={tab}
              onChange={setTab}
              tabs={[
                { id: 'links', label: <span data-asset-share-tab="links">Links</span> },
                { id: 'overview', label: <span data-asset-share-tab="overview">Overview</span> },
              ]}
            />
          </span>
        ) : (
          'Share for review'
        )
      }
    >
      {overview ? (
        <>
          <div className="-mt-2 mb-3 flex justify-end">
            <Link to={`/orgs/${asset.orgId}/assets/${asset.id}/review`} className="inline-flex items-center gap-1 text-xs text-accent-2 hover:underline" onClick={onClose} data-asset-review-open-page>
              <ExternalLink size={12} /> Open as a page
            </Link>
          </div>
          <AssetReviewPanel asset={asset} links={links} />
          <AssetReviewOverview asset={asset} links={links} />
        </>
      ) : (
        <>
          <p className="mb-4 text-sm text-muted">
            Send a link to “{asset.name}”. Reviewers {asset.kind === 'image' ? 'look at it' : 'watch it'} without an account and comment{pro ? ', draw on it, and approve it or ask for changes' : ''}; each
            link has its own conversation, which you answer here.
          </p>
          {ready ? <AssetReviewPanel asset={asset} links={links} /> : null}
          {!ready ? (
            <div className="py-6 text-center text-sm text-muted">{asset.status === 'ready' ? 'This kind of file cannot be shared for review.' : 'The file can be shared once it has been processed.'}</div>
          ) : (
            <>
              {sorted.length ? (
                <div className="mb-3 flex items-center gap-2">
                  <Select className="min-w-0 flex-1" value={creating ? '' : selectedId} onChange={(e) => (setSelectedId(e.target.value), setCreating(false))} aria-label="Review link" data-review-link-select>
                    {creating ? <option value="">New link</option> : null}
                    {sorted.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.label}
                        {linkState(l) !== 'active' ? ` (${linkState(l) === 'off' ? 'off' : 'expired'})` : ''}
                      </option>
                    ))}
                  </Select>
                  {!creating ? (
                    <Button size="sm" variant="secondary" onClick={() => setCreating(true)} data-new-review-link>
                      <Plus size={13} /> New link
                    </Button>
                  ) : null}
                </div>
              ) : null}
              {creating ? (
                <NewAssetLink
                  asset={asset}
                  maxDays={maxDays}
                  pro={pro}
                  onCreated={(id) => (setSelectedId(id), setCreating(false))}
                  onCancel={sorted.length ? () => setCreating(false) : undefined}
                />
              ) : !chosen ? (
                <div className="py-8 text-center text-xs text-faint">Loading…</div>
              ) : selected ? (
                <LinkCard
                  link={selected}
                  jobs={new Map()}
                  snapshots={[]}
                  target={{}}
                  what=""
                  pro={pro}
                  maxDays={maxDays}
                  fps={asset.fps || 30}
                  focusCommentId={selected.id === focusLinkId ? commentId : undefined}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </Modal>
  );
}

function NewAssetLink({ asset, maxDays, pro, onCreated, onCancel }: { asset: Asset; maxDays: number; pro: boolean; onCreated: (id: string) => void; onCancel?: () => void }) {
  const [label, setLabel] = useState('');
  const [comments, setComments] = useState(true);
  const [decisions, setDecisions] = useState(true);
  const [expiry, setExpiry] = useState<Expiry>({ kind: 'days', days: Math.min(30, maxDays) });
  const [password, setPasswordText] = useState('');
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    const id = uuidv7();
    const token = randomToken();
    try {
      await reducers().createAssetReviewLink({
        id,
        token,
        assetId: asset.id,
        label: label.trim(),
        allowComments: comments,
        allowDecisions: decisions && pro,
        expiresAtMs: expiryMs(expiry),
      });
      if (password) await setPassword(id, password);
      copy(reviewUrl(token));
      onCreated(id);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-xl border border-line bg-panel-2 p-3" data-new-link-form>
      <Input placeholder={`Label for reviewers (default: ${asset.name})`} value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
      <div className="flex flex-wrap items-center gap-3">
        <Toggle checked={comments} onChange={setComments} label="Comments" />
        <span title={pro ? undefined : 'Approvals are part of Pro'} data-new-link-approvals>
          <Toggle checked={decisions && pro} onChange={setDecisions} disabled={!pro} label="Approvals" />
        </span>
        <ExpiryPicker value={expiry} onChange={setExpiry} maxDays={maxDays} />
      </div>
      <div className="flex items-center gap-2">
        <Lock size={14} className="shrink-0 text-muted" />
        <Input
          type="password"
          autoComplete="new-password"
          placeholder={pro ? 'Password (optional)' : 'Password protection is part of Pro'}
          disabled={!pro}
          value={password}
          onChange={(e) => setPasswordText(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          data-new-link-password
        />
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line pt-3">
        {onCancel ? (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button size="sm" variant="primary" loading={busy} disabled={!!password && password.length < 4} onClick={() => void create()} data-create-review-link>
          <Link2 size={14} /> Create link
        </Button>
      </div>
    </div>
  );
}
